import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Mail, Clock, CheckCircle2 } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import { Reveal } from "@/components/Reveal";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const topics = ["Payment question", "Account balance", "Payoff quote", "Financial hardship", "Title / lien release", "Other"];

const inputCls =
  "bg-white border-slate-900/15 text-slate-900 placeholder:text-slate-600 focus-visible:ring-ember focus-visible:border-ember h-12";

const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", account_number: "", topic: "", message: "", sms_consent: false });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(null);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target ? e.target.value : e });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/contact`, form);
      setDone(data);
      toast.success("Message received. We'll be in touch within one business day.");
    } catch (err) {
      toast.error("Something went wrong sending your message. Please try again or call us.");
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <PageHeader
        testid="contact-header"
        overline="Customer Service"
        title="Talk to a"
        accent="real person."
        description="Send us a message and a Concorde Auto Acceptance representative will respond within one business day. For urgent payment matters, call us directly."
      />
      <section className="pb-24 lg:pb-32" data-testid="contact-body">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-2 gap-16">
          <Reveal>
            {done ? (
              <motion.div
                data-testid="contact-confirmation"
                initial={{ opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: 1 }}
                className="rounded-2xl border border-ember/40 bg-white/60 backdrop-blur-xl p-10"
              >
                <CheckCircle2 size={40} className="text-ember" />
                <h2 className="mt-6 font-display font-bold text-3xl text-slate-900">Message received.</h2>
                <p className="mt-4 text-slate-600 leading-relaxed">{done.message}</p>
                <p className="mt-6 font-mono text-xs text-slate-600">
                  REFERENCE <span className="text-slate-700">{done.id.slice(0, 8).toUpperCase()}</span>
                </p>
              </motion.div>
            ) : (
              <form onSubmit={submit} className="space-y-6" data-testid="contact-form">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="name" className="text-slate-700">Full name</Label>
                    <Input id="name" data-testid="contact-name-input" required minLength={2} className={inputCls} value={form.name} onChange={set("name")} placeholder="Jordan Rivera" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-slate-700">Email</Label>
                    <Input id="email" type="email" data-testid="contact-email-input" required className={inputCls} value={form.email} onChange={set("email")} placeholder="you@example.com" />
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="phone" className="text-slate-700">Phone (optional)</Label>
                    <Input id="phone" data-testid="contact-phone-input" className={inputCls} value={form.phone} onChange={set("phone")} placeholder="(555) 000-0000" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="account" className="text-slate-700">Account number (optional)</Label>
                    <Input id="account" data-testid="contact-account-input" className={`${inputCls} font-mono`} value={form.account_number} onChange={set("account_number")} placeholder="CAA-000000" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-700">Topic</Label>
                  <Select required onValueChange={(v) => setForm({ ...form, topic: v })}>
                    <SelectTrigger data-testid="contact-topic-select" className={`${inputCls} w-full`}>
                      <SelectValue placeholder="What is this about?" />
                    </SelectTrigger>
                    <SelectContent className="bg-white border-slate-900/15 text-slate-900">
                      {topics.map((t) => (
                        <SelectItem key={t} value={t} data-testid={`contact-topic-${t.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                          {t}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="message" className="text-slate-700">Message</Label>
                  <Textarea id="message" data-testid="contact-message-input" required minLength={10} rows={6} className={`${inputCls} h-auto`} value={form.message} onChange={set("message")} placeholder="How can we help with your account?" />
                </div>
                <div className="flex items-start gap-3" data-testid="contact-sms-consent">
                  <Checkbox
                    id="sms_consent"
                    data-testid="contact-sms-checkbox"
                    checked={form.sms_consent}
                    onCheckedChange={(v) => setForm({ ...form, sms_consent: v === true })}
                    className="mt-1 border-slate-900/25 data-[state=checked]:bg-ember data-[state=checked]:border-ember"
                  />
                  <Label htmlFor="sms_consent" className="text-sm text-slate-600 leading-relaxed font-normal cursor-pointer">
                    I consent to receive text messages from Concorde Auto Acceptance about my account, including payment requests and account status notifications. Message and data rates may apply. Message frequency varies. Reply STOP to opt out at any time. Consent is not a condition of service.
                  </Label>
                </div>
                <button
                  type="submit"
                  disabled={sending}
                  data-testid="contact-submit-button"
                  className="w-full sm:w-auto rounded-full bg-ember px-10 py-4 font-bold text-white transition-all duration-300 hover:bg-tangerine hover:shadow-[0_0_32px_rgba(255,87,34,0.45)] disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {sending ? "Sending…" : "Send Message"}
                </button>
              </form>
            )}
          </Reveal>

          <div className="lg:pl-8">
            <Reveal delay={0.1}>
              <p className="font-mono text-xs tracking-[0.28em] uppercase text-slate-600">Prefer to talk?</p>
              <a href="tel:+12142714800" data-testid="contact-phone-display" className="mt-6 block font-display font-bold tracking-tight text-4xl sm:text-5xl text-slate-900 hover:text-ember transition-colors duration-300">
                (214) 271-4800
              </a>
              <div className="mt-12 space-y-8">
                <div className="flex items-start gap-4" data-testid="contact-hours-block">
                  <Clock size={20} className="text-ember mt-1" />
                  <div>
                    <p className="font-bold text-slate-800">Hours of operation</p>
                    <p className="mt-1 text-sm text-slate-600">Monday – Saturday, 9:00 AM – 6:00 PM CT</p>
                  </div>
                </div>
                <div className="flex items-start gap-4" data-testid="contact-email-block">
                  <Mail size={20} className="text-ember mt-1" />
                  <div>
                    <p className="font-bold text-slate-800">Email</p>
                    <p className="mt-1 text-sm text-slate-600">
                      <a href="mailto:virginia.sanjuan@concordeaa.com" className="hover:text-ember transition-colors duration-300" data-testid="contact-email-link">virginia.sanjuan@concordeaa.com</a>
                    </p>
                  </div>
                </div>
              </div>
            </Reveal>
          </div>
        </div>

        <Reveal delay={0.15}>
          <div className="mt-20" data-testid="contact-map-section">
            <p className="font-mono text-xs tracking-[0.28em] uppercase text-slate-600">Visit Us</p>
            <p className="mt-3 font-display font-bold text-2xl text-slate-900 tracking-tight">
              908 S. Buckner Blvd, Dallas, TX 75217
            </p>
            <div className="mt-6 rounded-2xl border border-slate-900/10 overflow-hidden bg-white" data-testid="contact-map-wrapper">
              <iframe
                src="https://www.google.com/maps?q=908+S+Buckner+Blvd,+Dallas,+TX+75217&output=embed"
                title="Concorde Auto Acceptance — 908 S. Buckner Blvd, Dallas, TX 75217"
                data-testid="contact-map"
                className="w-full h-[420px] border-0"
                loading="lazy"
              />
            </div>
          </div>
        </Reveal>
      </section>
    </>
  );
};

export default Contact;
